import React, { useCallback, useState, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import UserCreditsWidget from '../components/UI/UserCreditsWidget';
import { useCanvas } from '../hooks/useCanvas';
import useCanvasStore from '../stores/canvasStore';
import { useCanvasElements } from '../hooks/useCanvasElements';
import { useOpenAgProject } from '../hooks/useOpenAgProject';
import { useAutosave } from '../hooks/useAutosave';
import useDesktopCheck from '../hooks/useDesktopCheck';
import LiteCanvasArea from '../components/CanvasLite/LiteCanvasArea';
import LitePropertiesPanel from '../components/CanvasLite/LitePropertiesPanel';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import LoadingProgressModal from '../components/UI/LoadingProgressModal';
import ComponentUpdateIndicator from '../components/UI/ComponentUpdateIndicator';
import DesktopRequiredScreen from '../components/UI/DesktopRequiredScreen';
import AGLogo from '../assets/Autograph-logo-01-01.svg';
import { projectsAPI } from '../services/api';
import {
  captureCanvasThumbnailAsBlob,
} from '../utils/canvasThumbnailUtils';
import { Check, Loader2, AlertCircle, Save } from 'lucide-react';

const LiteViewPage = () => {
  const isMobileOrPortrait = useDesktopCheck();
  const { projectId } = useParams();

  // Track stage bounds from LiteCanvasArea so we can position the save status on top of the stage
  const [liteStageBounds, setLiteStageBounds] = useState(null);
  const canvasWrapperRef = React.useRef(null);
  const canvasAreaRef = React.useRef(null);

  // Briefly flash the timeline controls (called by LitePropertiesPanel on nav)
  const revealTimelineControls = useCallback(() => {
    canvasAreaRef.current?.revealControls();
  }, []);

  const handleStageBoundsChange = useCallback((bounds) => {
    setLiteStageBounds(bounds);
  }, []);

  // Get canvas state and operations
  const {
    projectName,
    setProjectName,
    viewport,
    pixiInitialized,
    setCanvasSize,
    setCanvasBackgroundColor,
    clearHistory,
    updateElement,
    canvasElements,
    canvasSize,
    canvasBackgroundColor,
    pixiApp,
    updateSpritesForTime,
    currentTime,
    duration,
    isPlaying,
  } = useCanvas();

  const totalDuration = duration - 3;

  // Canvas elements hook (needed for project loading + component update toasts)
  // liteMode disables transform controls, hover bounding boxes, and viewport click handlers
  const {
    addComponentToCanvas,
    componentUpdateItems,
    replaceElementMedia,
  } = useCanvasElements(viewport, { liteMode: true });

  // Load the project
  const { projectData, isLoading, initializationError, loadingProgress } = useOpenAgProject({
    setProjectName,
    setCanvasSize,
    setCanvasBackgroundColor,
    viewport,
    pixiInitialized,
    clearHistory,
    updateElement,
    addComponentToCanvas,
  });

  // ── Autosave ──────────────────────────────────────────────────────────────
  const saveProject = useCallback(async () => {
    try {
      // Don't save if project load had failures - prevents overwriting good layers with incomplete state
      const storeState = useCanvasStore.getState();
      if (storeState.projectLoadFailed) {
        console.warn('🛡️ Lite save blocked - project load had failures, preventing layer data loss');
        return;
      }

      // Don't save if we have fewer layers than expected
      if (storeState.expectedLayerCount > 0 && canvasElements.length < storeState.expectedLayerCount) {
        console.warn(`🛡️ Lite save blocked - canvas has ${canvasElements.length} layers but project expects ${storeState.expectedLayerCount}. Preventing potential layer data loss.`);
        return;
      }

      // Save project data FIRST (prioritize data persistence over thumbnail)
      const projectData = {
        id: projectId,
        name: projectName,
        canvas: {
          width: canvasSize.width,
          height: canvasSize.height,
          backgroundColor: canvasBackgroundColor || '#000000',
        },
        duration: totalDuration,
        layers: canvasElements.map((element) => {
          const { pixiSprite, captionSprite, ...rest } = element;
          return rest;
        }),
      };

      await projectsAPI.saveProject(projectData);

      // Generate and upload thumbnail IN THE BACKGROUND (non-blocking)
      // This runs after the project data is already safely saved
      if (!isPlaying && viewport && pixiApp) {
        (async () => {
          try {
            const thumbnailBlob = await captureCanvasThumbnailAsBlob({
              viewport,
              canvasSize,
              pixiApp,
              updateSpritesForTime,
              totalDuration,
              currentTime,
              captureTime: currentTime,
              width: 640,
              height: 360,
              format: 'image/jpeg',
              quality: 0.8,
            });

            const thumbnailFilename = `${projectId}_thumbnail.jpg`;
            const uploadResponse = await projectsAPI.uploadThumbnail(
              thumbnailBlob,
              projectId,
              thumbnailFilename,
            );

            const thumbnailUrl = uploadResponse.data.data.thumbnailUrl;
            const thumbnailKey = uploadResponse.data.data.thumbnailKey;
            const filename = uploadResponse.data.data.filename;

            // Save thumbnail metadata in a lightweight follow-up call
            await projectsAPI.saveProject({
              id: projectId,
              thumbnailUrl,
              thumbnailKey,
              thumbnailFilename: filename,
            });
            console.log('Lite thumbnail saved in background successfully');
          } catch (thumbErr) {
            // Thumbnail failure is non-fatal - project data is already saved
            console.warn('Lite background thumbnail failed (non-fatal):', thumbErr);
          }
        })();
      }
    } catch (error) {
      console.error('Lite autosave failed:', error);
      throw error; // let useAutosave handle status
    }
  }, [
    projectId,
    projectName,
    canvasElements,
    canvasSize,
    canvasBackgroundColor,
    viewport,
    pixiApp,
    updateSpritesForTime,
    totalDuration,
    currentTime,
    isPlaying,
  ]);

  // Show mobile/portrait restriction screen
  // if (isMobileOrPortrait) {
  //   return <DesktopRequiredScreen feature="the lite viewer" />;
  // }

  // Show loading spinner while project is loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-ag-black-800">
        <div className="flex flex-col items-center justify-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-400 mt-4">Loading project...</p>
        </div>
      </div>
    );
  }

  // Show error if project failed to load
  if (initializationError) {
    return (
      <div className="flex items-center justify-center h-screen bg-ag-black-800">
        <div className="text-center">
          <p className="text-red-500 mb-2">Failed to load project</p>
          <p className="text-gray-400">{initializationError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-ag-black-800">
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-2 sm:px-4 py-1.5 sm:py-2 ml-12">
        {/* Left: full Autograph logo */}
        <Link to="/dashboard" className="flex items-center group">
          <img
            src={AGLogo}
            alt="Autograph"
            className="h-8 sm:h-8 md:h-10 w-auto transition-opacity group-hover:opacity-90"
          />
        </Link>

        {/* Right: user credits widget */}
        <UserCreditsWidget />
      </div>

      {/* Canvas area with embedded timeline controls */}
      <div ref={canvasWrapperRef} className="flex-1 min-h-0  relative">
        <LiteCanvasArea ref={canvasAreaRef} onStageBoundsChange={handleStageBoundsChange} />

        {/* Save status — positioned directly on top-left of the canvas stage */}
        {liteStageBounds && (
          <div
            style={{
              position: 'absolute',
              left: Math.max(4, liteStageBounds.left),
              top: Math.max(-10, liteStageBounds.top - 28),
              zIndex: 20,
              pointerEvents: 'auto',
            }}
          >
            <LiteAutosaveStatus canvasElements={canvasElements} saveProject={saveProject} />
          </div>
        )}
      </div>

      {/* Properties panel — sits directly below the Pixi stage */}
      <LitePropertiesPanel replaceElementMedia={replaceElementMedia} onNavigate={revealTimelineControls} />

      {/* Loading Progress Modal for Projects */}
      <LoadingProgressModal
        isOpen={loadingProgress?.isOpen || false}
        progress={loadingProgress?.progress || 0}
        currentAsset={loadingProgress?.currentAsset || ''}
        totalAssets={loadingProgress?.totalAssets || 0}
        loadedAssets={loadingProgress?.loadedAssets || 0}
        stage={loadingProgress?.stage || 'initializing'}
      />

      {/* Component Update Indicator — toast notifications for AG component re-renders */}
      <ComponentUpdateIndicator updateItems={componentUpdateItems} />
    </div>
  );
};

// ── Autosave status badge (memoised to avoid parent re-renders) ─────────────
const LiteAutosaveStatus = memo(({ canvasElements, saveProject }) => {
  const { isSaving, lastSaved, saveStatus } = useAutosave(canvasElements, saveProject, 2000);

  const formatLastSaved = (date) => {
    if (!date) return '';
    const diff = Math.floor((Date.now() - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-1 bg-ag-black-800/80 backdrop-blur-sm rounded-md sm:rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 border border-ag-black-400/40">
      {saveStatus === 'saving' && (
        <>
          <Loader2 size={12} className="text-ag-green animate-spin sm:hidden" />
          <Loader2 size={14} className="text-ag-green animate-spin hidden sm:block" />
          <span className="text-[10px] sm:text-xs text-ag-green">Saving…</span>
        </>
      )}
      {saveStatus === 'saved' && (
        <>
          <Check size={12} className="text-ag-green sm:hidden" />
          <Check size={14} className="text-ag-green hidden sm:block" />
          <span className="text-[10px] sm:text-xs text-ag-green">Saved {formatLastSaved(lastSaved)}</span>
        </>
      )}
      {saveStatus === 'error' && (
        <>
          <AlertCircle size={12} className="text-red-400 sm:hidden" />
          <AlertCircle size={14} className="text-red-400 hidden sm:block" />
          <span className="text-[10px] sm:text-xs text-red-400">Not saved</span>
        </>
      )}
      {saveStatus === 'idle' && lastSaved && (
        <>
          <Check size={12} className="text-ag-black-300 sm:hidden" />
          <Check size={14} className="text-ag-black-300 hidden sm:block" />
          <span className="text-[10px] sm:text-xs text-ag-black-300">Saved {formatLastSaved(lastSaved)}</span>
        </>
      )}
      {saveStatus === 'idle' && !lastSaved && (
        <>
          <Save size={12} className="text-gray-500 sm:hidden" />
          <Save size={14} className="text-gray-500 hidden sm:block" />
          <span className="text-[10px] sm:text-xs text-gray-500">Autosave</span>
        </>
      )}
    </div>
  );
});

LiteAutosaveStatus.displayName = 'LiteAutosaveStatus';

export default LiteViewPage;
